/**
 * ADR-0046 §5 (amended 2026-09-22): what the person card SHOWS, as data.
 *
 * One card replaces both person dialogs. Its three panels are the ADR's
 * areas — Identity, Contact, Access — each with its owner chip. This module
 * turns a Users & Roles row (plus the production person record, when there is
 * one) into exactly what the card renders, so the rules live here and vitest
 * can exercise them without Firebase.
 *
 * The test for every field (Ed, walked through 2026-09-22): does it change
 * what this person can do on our platform? Care, HR and clinical data are
 * never copied in — the card links out instead ("Family of X →").
 */
import { avatarStoragePath, relationshipLabel } from "../clients/family-contacts";
import { roleName } from "./access-labels";
import type { PersonProvenance } from "./loaders";
import {
  familyNames, sharesAddress, whyListed,
  type CatalogueRow, type FamilyLink,
} from "./user-catalogue";

/** What the card reads from the production `_person`, when there is one. */
export interface PersonFacts {
  active?: boolean;
  telecom?: unknown;
  address?: unknown;
  provenance?: PersonProvenance | null;
  /** The `_person`'s `photo` (ADR-0031 Attachment list). */
  photo?: unknown;
  /** Identity, for the Identity panel's edit form. */
  name?: unknown;
  gender?: unknown;
  birthDate?: unknown;
  /** From their `_practitioner`, when they have one (staff): its
   *  `qualification` list. Undefined = no practitioner record. */
  qualification?: unknown[];
}

// ── "Soma trained" (Ed, 2026-09-23/24) ────────────────────────────────────
// The date from which a carer owes in-app shift records. A first-class
// Practitioner.qualification entry; app.dsr's obligation logic reads
// `period.start`. ☠️ The system string is what makes the OneTouch sync keep
// the entry (agent.onetouch/mappers/qualifications.py) — never change it.
export const STC_QUALIFICATION_SYSTEM =
  "https://fhir.sweettreeconnect.com/CodeSystem/practitioner-qualification";
export const SOMA_TRAINED = "soma-trained";

export interface SomaLine {
  /** YYYY-MM-DD, or null when not recorded. */
  start: string | null;
  /** "14 July 2026", or "Not recorded". */
  label: string;
}

/** The carer's Soma-trained date; null when they have no carer record at all. */
export function somaTrained(facts: PersonFacts | null): SomaLine | null {
  if (!facts || !Array.isArray(facts.qualification)) return null;
  for (const q of entries(facts.qualification)) {
    const coding = entries((q.code as Record<string, unknown> | undefined)?.coding);
    if (coding.some((c) => c.system === STC_QUALIFICATION_SYSTEM && c.code === SOMA_TRAINED)) {
      const start = (q.period as Record<string, unknown> | undefined)?.start;
      if (typeof start === "string" && start) {
        const at = new Date(`${start}T12:00:00Z`);
        const label = Number.isNaN(at.getTime()) ? start
          : at.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
        return { start, label };
      }
    }
  }
  return { start: null, label: "Not recorded" };
}

/** Today's date in the UK, YYYY-MM-DD — the latest a training date can be. */
export function ukToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(now);
}

export type Owner = "OneTouch" | "STC" | null;

export interface CardField { label: string; value: string }

export interface AccessLine {
  label: string;
  value: string;
  /** Draw as an access chip (in effect) or as plain muted text. */
  inEffect: boolean;
}

export interface FamilyLine {
  client: string;
  relationship: string;
  state: string;
  inEffect: boolean;
  /** Link target for "Family of X →"; null → plain text. */
  eocId: string | null;
}

export interface CardView {
  name: string;
  initials: string;
  /** Storage path of their photo thumbnail; null → the initials show. */
  photoPath: string | null;
  why: string[];
  /** Null when there is no production person record to read it from. */
  employment: { active: boolean; owner: Owner } | null;
  identityOwner: Owner;
  contact: CardField[];
  contactOwner: Owner;
  production: AccessLine[];
  environments: AccessLine[];
  family: FamilyLine[];
  signIn: string;
  /** ADR-0046 §6: set when this card is a CLIENT's — their circle instead
   *  of access they hold, and a link to their client page. */
  client: ClientView | null;
  /** Their "Soma trained" date; null when they have no carer record. */
  soma: SomaLine | null;
}

export interface CircleLine {
  name: string;
  relationship: string;
  state: string;
  inEffect: boolean;
  relatedPersonDocId: string;
}

export interface ClientView {
  eocId: string;
  status: string;
  hasPlan: boolean;
  /** OMON: the live plan's version and whether a draft is in progress —
   *  both already on the planner's own client list, so the card's plan
   *  line costs no extra read. */
  planVersion: number | null;
  hasDraft: boolean;
  circle: CircleLine[];
}

/**
 * What a client's card shows beyond their circle: the same identity and
 * contact facts the client page shows, read live from their own records
 * (never copied here, ADR-0046 §6 — a stale copy of an address is worse
 * than a link). Care stays a one-line summary plus a link: the plan,
 * goals, charts and history live on the client page, which owns them.
 *
 * Missing facts SAY they are missing rather than disappearing: a blank
 * line reads as "nothing to know", and a gap in OneTouch is worth seeing.
 */
export interface ClientDetail {
  born: string;
  status: string;
  active: boolean;
  address: string;
  phone: string;
  area: string;
  careManager: string;
  plan: string;
  planNote: string | null;
  photoPath: string | null;
}

/** The subset of the client page's context a card needs (loaders/client-context). */
export interface ClientContextFacts {
  birthDate?: string;
  age?: number;
  addressSummary?: string;
  phone?: string;
  practice?: string;
  careManager?: string;
  clientAvatarPath?: string;
}

const NOT_RECORDED = "Not recorded";

/** "14 March 1946 · 80", or the missing-value line. A bad date is missing. */
function bornLine(birthDate?: string, age?: number): string {
  const at = birthDate ? new Date(birthDate) : null;
  if (!at || Number.isNaN(at.getTime())) return "Date of birth not recorded";
  const shown = at.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  return typeof age === "number" ? `Born ${shown} · ${age}` : `Born ${shown}`;
}

/** "active" → "Active client"; anything else reads as itself ("On hold"). */
function statusLine(status: string): string {
  const s = (status || "").replace(/[-_]/g, " ").trim();
  if (!s) return "Status not recorded";
  if (s.toLowerCase() === "active") return "Active client";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function clientDetail(client: ClientView, ctx: ClientContextFacts | null): ClientDetail {
  const plan = client.hasPlan
    ? `Outcome monitoring plan${client.planVersion ? ` · v${client.planVersion}` : ""}`
    : "No outcome monitoring plan yet";
  return {
    born: bornLine(ctx?.birthDate, ctx?.age),
    status: statusLine(client.status),
    active: (client.status || "").toLowerCase() === "active",
    address: ctx?.addressSummary || "Address not recorded",
    phone: ctx?.phone || NOT_RECORDED,
    area: ctx?.practice || NOT_RECORDED,
    careManager: ctx?.careManager || "Not assigned",
    plan,
    planNote: client.hasDraft ? "A draft is in progress." : null,
    photoPath: ctx?.clientAvatarPath ?? null,
  };
}

const OWNER: Record<string, Owner> = { onetouch: "OneTouch", stc: "STC" };

function ownerOf(prov: PersonProvenance | null | undefined, area: string): Owner {
  return OWNER[prov?.stewards?.[area] ?? ""] ?? null;
}

export function initials(name: string | null | undefined): string {
  const words = (name ?? "").replace(/\(.*?\)/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function entries(value: unknown): Record<string, unknown>[] {
  const list = Array.isArray(value) ? value
    : value && typeof value === "object" ? Object.values(value as Record<string, unknown>) : [];
  return list.filter((t): t is Record<string, unknown> => !!t && typeof t === "object");
}

const usable = (v: unknown): v is string =>
  typeof v === "string" && v.trim() !== "" && v.trim() !== "unused";

/** First usable value of one telecom channel. */
export function telecomValue(telecom: unknown, system: "phone" | "email"): string | null {
  for (const t of entries(telecom)) {
    if (String(t.system ?? "").toLowerCase() === system && usable(t.value)) return t.value.trim();
  }
  return null;
}

/** One line of address from a FHIR address list (or map); "unused" skipped. */
export function addressLine(address: unknown): string | null {
  const a = entries(address)[0];
  if (!a) return null;
  const lines = Array.isArray(a.line) ? a.line : [a.line];
  const parts = [...lines, a.city, a.postalCode].filter(usable).map((s) => s.trim());
  return parts.length > 0 ? parts.join(", ") : null;
}

function productionLines(row: CatalogueRow): AccessLine[] {
  return row.productionRoles.map((r) => {
    const role = r.role ? roleName(r.role) : "Unknown role";
    const scope = r.dataScope === "real" ? " · real data"
      : r.dataScope === "test" ? " · test data"
        : r.dataScope === "both" ? " · data both" : "";
    const status = r.status === "active" ? "" : ` · ${r.status}`;
    return { label: "Production", value: `${role}${scope}${status}`, inEffect: r.status === "active" };
  });
}

function environmentLines(row: CatalogueRow): AccessLine[] {
  return [...row.environments]
    .sort((a, b) => a.environment.localeCompare(b.environment))
    .map((e) => ({
      label: e.environment.charAt(0).toUpperCase() + e.environment.slice(1),
      value: `${roleName(e.role)}${e.status === "active" ? "" : ` · ${e.status}`}`,
      inEffect: e.status === "active",
    }));
}

const FAMILY_STATE: Record<FamilyLink["accessState"], string> = {
  "using": "using the app",
  "granted": "access given",
  "not-granted": "no access",
  "requested": "asked to join",
  "revoked": "access removed",
};

function familyLines(row: CatalogueRow): FamilyLine[] {
  return row.family.map((l) => ({
    client: l.clientName ?? "a client",
    relationship: l.relationships.map(relationshipLabel).join(" · ") || "contact",
    state: FAMILY_STATE[l.accessState],
    inEffect: l.accessState === "using" || l.accessState === "granted",
    eocId: l.eocId ?? null,
  }));
}

function contactFields(row: CatalogueRow, facts: PersonFacts | null): CardField[] {
  const email = row.email ?? telecomValue(facts?.telecom, "email")
    ?? row.family.map((l) => l.email).find((e): e is string => !!e) ?? null;
  const phone = telecomValue(facts?.telecom, "phone")
    ?? row.family.map((l) => l.phone).find((p): p is string => !!p) ?? null;
  const address = addressLine(facts?.address);
  return [
    { label: "email", value: email ?? "—" },
    { label: "mobile", value: phone ?? "—" },
    { label: "address", value: address ?? "—" },
  ];
}

/** How they get in, in words — never a credential. Carers keep passwords
 *  through the transition (ADR-0045 amendment); every other environment and
 *  Circle of Support sign in by email link or verified number. */
function signInLine(row: CatalogueRow): string {
  const parts: string[] = [];
  if (row.productionRoles.some((r) => r.status === "active")) parts.push("Password (production)");
  if (row.environments.some((e) => e.status === "active")) parts.push("Email link (other environments)");
  if (row.family.some((l) => l.accessState === "using" || l.accessState === "granted")) {
    parts.push("Circle of Support app");
  }
  return parts.length > 0 ? parts.join(" · ") : "No way in at the moment";
}

// ── PR 2c: the photo (ADR-0031 media plane, the same as the client card) ──

/** Staff: their `_person`'s photo. Family-only: the first of their contact
 *  records that has one (the photo the client card's Family & contacts shows). */
export function cardPhotoPath(row: CatalogueRow, facts: PersonFacts | null): string | null {
  if (row.personDocId) return avatarStoragePath({ photo: facts?.photo }) ?? null;
  return row.family.map((l) => l.photoPath).find((p): p is string => !!p) ?? null;
}

export interface PhotoOwner { collection: "_person" | "_relatedPerson"; docId: string }

/** Where a new photo is written — the agent.media owners (its
 *  OWNER_COLLECTIONS allows both). Staff: their `_person`. A family member:
 *  EVERY contact record of theirs, so each client's Family & contacts shows
 *  the same face. None → no Change photo button. */
export function photoOwners(row: CatalogueRow): PhotoOwner[] {
  if (row.personDocId) return [{ collection: "_person", docId: row.personDocId }];
  return row.family.map((l) => ({ collection: "_relatedPerson" as const, docId: l.relatedPersonDocId }));
}

export function buildCardView(row: CatalogueRow, facts: PersonFacts | null): CardView {
  const prov = facts?.provenance ?? null;
  const name = row.name ?? familyNames(row)[0] ?? row.email ?? "Unknown";
  return {
    name,
    initials: initials(name),
    photoPath: cardPhotoPath(row, facts),
    why: whyListed(row),
    employment: facts && row.productionRoles.length > 0
      ? { active: facts.active !== false, owner: ownerOf(prov, "employment") }
      : null,
    identityOwner: ownerOf(prov, "identity"),
    contact: contactFields(row, facts),
    contactOwner: ownerOf(prov, "contact"),
    production: productionLines(row),
    environments: environmentLines(row),
    family: familyLines(row),
    signIn: row.client ? "Clients don't sign in to SweetTree" : signInLine(row),
    soma: row.client ? null : somaTrained(facts),
    client: row.client
      ? {
          eocId: row.client.eocId,
          status: row.client.status,
          hasPlan: row.client.hasPlan,
          planVersion: row.client.planVersion ?? null,
          hasDraft: row.client.hasDraft ?? false,
          circle: row.client.circle.map((l) => ({
            name: l.name ?? "Unnamed contact",
            relationship: l.relationships.map(relationshipLabel).join(" · ") || "contact",
            state: FAMILY_STATE[l.accessState],
            inEffect: l.accessState === "using" || l.accessState === "granted",
            relatedPersonDocId: l.relatedPersonDocId,
          })),
        }
      : null,
  };
}

/** Previous / Next through the list AS FILTERED, so "check every carer in
 *  this area" is a walk, not a hunt. Ends are null, never wrap. */
export function neighbours(keys: string[], key: string): {
  prev: string | null; next: string | null; index: number; total: number;
} {
  const i = keys.indexOf(key);
  if (i < 0) return { prev: null, next: null, index: 0, total: keys.length };
  return {
    prev: i > 0 ? keys[i - 1] : null,
    next: i < keys.length - 1 ? keys[i + 1] : null,
    index: i + 1,
    total: keys.length,
  };
}

/** ☠️ A shared address is several people: the card opens a chooser, and
 *  each card is exactly one of them. */
export function needsChooser(row: CatalogueRow): boolean {
  return sharesAddress(row);
}

/** The row narrowed to one of the people sharing its address: their own
 *  family links only, and no staff or environment access (those belong to
 *  the address's staff owner, who has a name that is not theirs). */
export function rowForPerson(row: CatalogueRow, name: string): CatalogueRow {
  return {
    ...row,
    key: `${row.key}#${name}`,
    personDocId: null,
    name,
    productionRoles: [],
    environments: [],
    family: row.family.filter((l) => l.name === name),
    staffOrigin: null,
  };
}

// ── PR 2a: editing the Contact panel ─────────────────────────────────────

/** The contact values as they are now, raw — what the edit form starts
 *  from and compares against. */
export interface ContactValues {
  email: string;
  mobile: string;
  line: string;
  city: string;
  postalCode: string;
}

function addressParts(address: unknown): { line: string; city: string; postalCode: string } {
  const a = entries(address)[0] ?? {};
  const lineRaw = Array.isArray(a.line) ? a.line[0] : a.line;
  const part = (v: unknown) => (usable(v) ? v.trim() : "");
  return { line: part(lineRaw), city: part(a.city), postalCode: part(a.postalCode) };
}

export function currentContact(row: CatalogueRow, facts: PersonFacts | null): ContactValues {
  const email = row.email ?? telecomValue(facts?.telecom, "email")
    ?? row.family.map((l) => l.email).find((e): e is string => !!e) ?? "";
  const mobile = telecomValue(facts?.telecom, "phone")
    ?? row.family.map((l) => l.phone).find((p): p is string => !!p) ?? "";
  return { email, mobile, ...addressParts(facts?.address) };
}

/** Whose record the Contact edit writes: a staff person's `_person` (one
 *  update-person call, address included), or a family member's contact
 *  records (set-email + attach-number; their address stays in OneTouch).
 *  Neither → no Edit button. */
export function contactEditTarget(row: CatalogueRow): "staff" | "family" | null {
  if (row.personDocId) return "staff";
  if (row.family.length > 0) return "family";
  return null;
}

export type ContactPlan =
  | { kind: "none" }
  | { kind: "invalid"; message: string }
  | { kind: "staff"; fields: { email?: string; mobile?: string;
      address?: { line: string; city: string; postalCode: string } } }
  | { kind: "family"; email?: string; mobile?: string; relatedPersonDocIds: string[] };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Loose UK/E.164 shape; the server's normalise_phone is the real judge. */
function plausiblePhone(raw: string): boolean {
  const digits = raw.replace(/[\s()-]/g, "");
  return /^\+?\d{10,15}$/.test(digits);
}

/** What Save will send, from what the form holds. Only CHANGED values go;
 *  blank never deletes (the box can set, not remove). */
export function planContactEdit(
  row: CatalogueRow, before: ContactValues, form: ContactValues,
): ContactPlan {
  const target = contactEditTarget(row);
  if (!target) return { kind: "none" };
  const email = form.email.trim().toLowerCase();
  const mobile = form.mobile.trim();
  const emailChanged = email !== "" && email !== before.email.trim().toLowerCase();
  const mobileChanged = mobile !== "" && mobile !== before.mobile.trim();
  if (emailChanged && !EMAIL_RE.test(email)) {
    return { kind: "invalid", message: "That doesn't look like an email address." };
  }
  if (mobileChanged && !plausiblePhone(mobile)) {
    return { kind: "invalid", message: "That doesn't look like a UK mobile number." };
  }

  if (target === "family") {
    if (!emailChanged && !mobileChanged) return { kind: "none" };
    return {
      kind: "family",
      ...(emailChanged ? { email } : {}),
      ...(mobileChanged ? { mobile } : {}),
      relatedPersonDocIds: row.family.map((l) => l.relatedPersonDocId),
    };
  }

  const address = { line: form.line.trim(), city: form.city.trim(), postalCode: form.postalCode.trim() };
  const addressChanged = address.line !== before.line || address.city !== before.city
    || address.postalCode !== before.postalCode;
  if (addressChanged && address.line === "") {
    return { kind: "invalid", message: "An address needs at least its first line." };
  }
  const fields = {
    ...(emailChanged ? { email } : {}),
    ...(mobileChanged ? { mobile } : {}),
    ...(addressChanged ? { address } : {}),
  };
  return Object.keys(fields).length === 0 ? { kind: "none" } : { kind: "staff", fields };
}

// ── Editing the Identity panel (Ed, 2026-09-22: "Edit identity" opened the
// old dialog, unlike the other two panels) ─────────────────────────────────

export interface IdentityValues {
  given: string;
  family: string;
  /** "" | female | male | other | unknown */
  gender: string;
  /** YYYY-MM-DD or "" */
  birthDate: string;
}

export const GENDERS = ["", "female", "male", "other", "unknown"] as const;

export type IdentityPlan =
  | { kind: "none" }
  | { kind: "invalid"; message: string }
  | { kind: "save"; fields: { given?: string; family?: string; gender?: string; birthDate?: string } };

/** What Save sends: only changed values, and the name as a pair (the
 *  server rebuilds `name` from both). A name can't be emptied. */
export function planIdentityEdit(before: IdentityValues, form: IdentityValues): IdentityPlan {
  const given = form.given.trim();
  const family = form.family.trim();
  if (!given || !family) {
    return { kind: "invalid", message: "Both the first name and the family name are needed." };
  }
  if (form.birthDate && !/^\d{4}-\d{2}-\d{2}$/.test(form.birthDate)) {
    return { kind: "invalid", message: "That birth date isn't a date." };
  }
  if (!(GENDERS as readonly string[]).includes(form.gender)) {
    return { kind: "invalid", message: "Choose a gender from the list." };
  }
  const fields: { given?: string; family?: string; gender?: string; birthDate?: string } = {};
  if (given !== before.given.trim() || family !== before.family.trim()) {
    fields.given = given;
    fields.family = family;
  }
  if (form.gender !== before.gender) fields.gender = form.gender;
  if (form.birthDate !== before.birthDate) fields.birthDate = form.birthDate;
  return Object.keys(fields).length === 0 ? { kind: "none" } : { kind: "save", fields };
}

export interface ChooserOption {
  name: string;
  detail: string;
  row: CatalogueRow;
}

/** Who the chooser offers: each family member on the address, plus the
 *  address's staff / invited owner when there is one (their family links,
 *  if any, stay with them). */
export function chooserOptions(row: CatalogueRow): ChooserOption[] {
  const options: ChooserOption[] = [];
  const hasAccount = row.productionRoles.length > 0 || row.environments.length > 0;
  if (hasAccount && row.name) {
    options.push({
      name: row.name,
      detail: whyListed({ ...row, family: [] }).join(" · ") || "Staff",
      row: { ...row, family: row.family.filter((l) => l.name === row.name) },
    });
  }
  for (const name of familyNames(row)) {
    if (hasAccount && name === row.name) continue;
    const mine = row.family.filter((l) => l.name === name);
    const lines = mine.map((l) =>
      `${l.relationships.map(relationshipLabel).join(" · ") || "contact"} of ${l.clientName ?? "a client"}`,
    ).join("; ");
    options.push({
      name,
      // The line STARTS with the relationship, so it takes a capital.
      detail: lines.charAt(0).toUpperCase() + lines.slice(1),
      row: rowForPerson(row, name),
    });
  }
  return options;
}
